/**
 * DealInfoEditDialog - Modal para editar informações do negócio
 * Permite alterar Pipeline, Fase, Previsão de Fechamento e Origem
 *
 * Versão: 2.0.0
 * RELEASE: 2026-07-11 v2.0.0 - Permite selecionar pipeline de OUTRA workspace
 *          do mesmo tenant (movimentação cross-workspace). Agrupa pipelines
 *          por workspace no combobox e exibe aviso quando a mudança implica
 *          em transferir o negócio de workspace.
 * RELEASE: 2026-02-09 v1.0.0 - Versão inicial
 *
 * REGRA: Ao trocar Pipeline, o campo Fase é limpo para forçar seleção.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, Check, ChevronsUpDown, Layers, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CrmDeal } from '@/hooks/useCrmDeals';
import { useTenantWorkspacesPipelines } from '@/hooks/useTenantWorkspacesPipelines';
import { getLeadSourceIcon } from '@/lib/lead-sources';
import { useLeadSources } from '@/hooks/useLeadSources';
import { PhaseSelector } from '@/components/crm/PhaseSelector';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

// Props do componente
interface DealInfoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: CrmDeal;
  onSave: (data: {
    pipeline_id: string;
    phase_id: string;
    expected_close_date: string | null;
    source: string | null;
  }) => void;
  isSaving?: boolean;
}

/**
 * Modal para editar informações do negócio
 * Gerencia estado local isolado até o momento de salvar
 */
export function DealInfoEditDialog({
  open,
  onOpenChange,
  deal,
  onSave,
  isSaving = false,
}: DealInfoEditDialogProps) {
  // Estados locais (isolados do estado global até salvar)
  const [selectedPipelineId, setSelectedPipelineId] = useState(deal.pipeline_id);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(deal.phase_id);
  const [expectedCloseDate, setExpectedCloseDate] = useState<Date | undefined>(
    deal.expected_close_date ? new Date(deal.expected_close_date) : undefined
  );
  const [source, setSource] = useState(deal.source || '');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [pipelineOpen, setPipelineOpen] = useState(false);

  // Catálogo dinâmico de origens (sistema + custom do tenant)
  const { activeSources: leadSources } = useLeadSources();

  // Busca TODAS as workspaces + pipelines do tenant (permite cross-workspace)
  const { data: workspaces = [], isLoading: loadingPipelines } = useTenantWorkspacesPipelines(
    { enabled: open }
  );

  // Achata a lista de pipelines mantendo referência à workspace
  const pipelines = useMemo(
    () => workspaces.flatMap((ws) => ws.pipelines.map((p) => ({ ...p, workspace_name: ws.name }))),
    [workspaces]
  );

  // Reset estados ao abrir modal
  useEffect(() => {
    if (open) {
      setSelectedPipelineId(deal.pipeline_id);
      setSelectedPhaseId(deal.phase_id);
      setExpectedCloseDate(deal.expected_close_date ? new Date(deal.expected_close_date) : undefined);
      setSource(deal.source || '');
    }
  }, [open, deal]);

  /**
   * Handler: Troca de pipeline
   * REGRA: Se pipeline mudar, LIMPA fase para forçar nova seleção
   */
  const handlePipelineChange = (newPipelineId: string) => {
    setSelectedPipelineId(newPipelineId);
    
    // Se mudou para outra pipeline, OBRIGA selecionar nova fase
    if (newPipelineId !== deal.pipeline_id) {
      setSelectedPhaseId(null); // Limpa para forçar seleção
    } else {
      // Voltou para pipeline original, restaura fase original
      setSelectedPhaseId(deal.phase_id);
    }
  };

  /**
   * Handler: Seleção de fase
   */
  const handlePhaseSelect = (phaseId: string) => {
    setSelectedPhaseId(phaseId);
  };

  /**
   * Handler: Seleção de data
   */
  const handleDateSelect = (date: Date | undefined) => {
    setExpectedCloseDate(date);
    setCalendarOpen(false);
  };

  /**
   * Handler: Limpar data
   */
  const handleClearDate = () => {
    setExpectedCloseDate(undefined);
    setCalendarOpen(false);
  };

  // Validação: Fase deve estar selecionada
  const isValid = !!selectedPhaseId;

  // Verifica se houve alterações
  const hasChanges = 
    selectedPipelineId !== deal.pipeline_id ||
    selectedPhaseId !== deal.phase_id ||
    (expectedCloseDate ? format(expectedCloseDate, 'yyyy-MM-dd') : null) !== deal.expected_close_date ||
    (source || null) !== (deal.source || null);

  /**
   * Handler: Salvar alterações
   */
  const handleSave = () => {
    if (!isValid) return;
    
    onSave({
      pipeline_id: selectedPipelineId,
      phase_id: selectedPhaseId!,
      expected_close_date: expectedCloseDate ? format(expectedCloseDate, 'yyyy-MM-dd') : null,
      source: source || null,
    });
  };

  // Indica se pipeline foi alterada (para mostrar warning na fase)
  const pipelineChanged = selectedPipelineId !== deal.pipeline_id;

  // Pipeline atualmente selecionada
  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId);

  // Detecta troca de workspace (cross-workspace move)
  const currentWorkspaceId = deal.pipeline?.workspace_id || null;
  const targetWorkspaceId = selectedPipeline?.workspace_id || null;
  const workspaceChanged =
    !!targetWorkspaceId && !!currentWorkspaceId && targetWorkspaceId !== currentWorkspaceId;
  const targetWorkspaceName = selectedPipeline?.workspace_name;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Editar Informações</DialogTitle>
          <DialogDescription>
            Altere pipeline, fase, data prevista ou origem do negócio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Pipeline - agrupado por workspace (v2.0.0) */}
          <div className="space-y-2">
            <Label htmlFor="pipeline">Pipeline *</Label>
            <Popover open={pipelineOpen} onOpenChange={setPipelineOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={pipelineOpen}
                  disabled={loadingPipelines}
                  className="w-full justify-between font-normal"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">
                      {selectedPipeline
                        ? `${selectedPipeline.workspace_name} • ${selectedPipeline.name}`
                        : 'Selecione...'}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandList>
                    <CommandEmpty>Nenhuma pipeline encontrada.</CommandEmpty>
                    {workspaces.map((ws) => (
                      <CommandGroup key={ws.id} heading={ws.name}>

                        {ws.pipelines.map((pipeline) => {
                          const activePhases = pipeline.phases?.filter(p => !p.is_won && !p.is_lost).length || 0;
                          const isSelected = selectedPipelineId === pipeline.id;
                          return (
                            <CommandItem
                              key={pipeline.id}
                              value={`${ws.name} ${pipeline.name}`}
                              onSelect={() => {
                                handlePipelineChange(pipeline.id);
                                setPipelineOpen(false);
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                                <span className="flex-1">{pipeline.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {activePhases} {activePhases === 1 ? 'fase' : 'fases'}
                                </span>
                              </div>
                              <Check
                                className={cn(
                                  'ml-2 h-4 w-4',
                                  isSelected ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Aviso de mudança de workspace */}
            {workspaceChanged && (
              <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-amber-900 dark:text-amber-100">
                  <p className="font-medium">Este negócio será movido para outra área de trabalho.</p>
                  <p className="mt-0.5 text-amber-800/80 dark:text-amber-200/80">
                    Destino: <span className="font-medium">{targetWorkspaceName}</span>. As permissões passarão
                    a seguir a matriz da nova fase. Produtos que não pertencem à nova área continuarão
                    listados no negócio, mas ficarão marcados como não disponíveis no catálogo.
                  </p>
                </div>
              </div>
            )}
          </div>


          {/* Fase - depende do pipeline selecionado */}
          <div className="space-y-2">
            <Label htmlFor="phase">Fase *</Label>
            <PhaseSelector
              pipelineId={selectedPipelineId}
              value={selectedPhaseId}
              onSelect={handlePhaseSelect}
              placeholder="Selecione a fase..."
              className={cn(
                !selectedPhaseId && pipelineChanged && 'border-amber-500 focus:border-amber-500'
              )}
            />
            {/* Aviso quando pipeline foi alterada e fase não selecionada */}
            {!selectedPhaseId && pipelineChanged && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                ⚠️ Selecione uma fase para a nova pipeline
              </p>
            )}
          </div>

          {/* Previsão de Fechamento */}
          <div className="space-y-2">
            <Label>Previsão de Fechamento</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !expectedCloseDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {expectedCloseDate 
                    ? format(expectedCloseDate, 'dd/MM/yyyy', { locale: ptBR })
                    : 'Selecione uma data...'
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={expectedCloseDate}
                  onSelect={handleDateSelect}
                  locale={ptBR}
                  initialFocus
                />
                {expectedCloseDate && (
                  <div className="p-2 border-t">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full text-xs"
                      onClick={handleClearDate}
                    >
                      Limpar data
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>

          {/* Origem */}
          <div className="space-y-2">
            <Label htmlFor="source">Origem</Label>
            <Select value={source || 'none'} onValueChange={(val) => setSource(val === 'none' ? '' : val)}>
              <SelectTrigger id="source">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">Nenhuma</span>
                </SelectItem>
                {leadSources.map(s => {
                  const Icon = getLeadSourceIcon(s.id);
                  return (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                        <span>{s.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!isValid || isSaving || !hasChanges}
          >
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
